import React, { useRef, useState, useContext, useEffect } from "react";
import styles from "./styles/Customization.module.css";
import InputText from "../../components/ui/input_text/InputText";
import Button from "../../components/ui/button/Button";
import DataContext from "../../context/DataContext";
import { axiosSC, axiosSWLogIn } from "../../api/axiosDefaults";
import OutputWindow from "../../components/ui/output_window/OutputWindow";
import Spinner from "../../components/ui/spinner/Spinner";
import { useTranslation } from "react-i18next";
import { encryptor } from "../../utils/utils";

/**
 * The LogIn component
 * Handles loging in to customization page
 */

const LogIn = ({
    isAdmin, // boolean - is the user an admin
    setIsAdmin, // function - set the admin state
    setIsSuperUser,
}) => {
    const { t } = useTranslation();

    const { handleError, currentCustom, connection } = useContext(DataContext);
    const userNameRef = useRef(null);
    const [userData, setUserData] = useState({
        userName: "",
        userPassword: "",
    });
    const [hoverFlag, setHoverFlag] = useState(false);
    const [inProcess, setInProcess] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formButtonDisable, setFormButtonDisable] = useState(true);

    // Set focus on the user name input
    useEffect(() => {
        userNameRef.current && userNameRef.current.focus();
    }, []);

    // Set form button state based on the user input
    useEffect(() => {
        setFormButtonDisable(!(userData?.userName && userData?.userPassword));
    }, [userData]);

    /**
     * Reset the form data
     */
    const handleReset = () => {
        setSuccess(false);
        setUserData({
            userName: "",
            userPassword: "",
        });
        setHoverFlag(false);
    };

    // /**
    //  * Submit handle
    //  * Encrypt the user password
    //  * and submit the user input and the intKey to the SC server end point for authintication
    //  * the intKey is the key that is used to encrypt the user password
    //  * @param {event} event the event object
    //  */
    // const handleSubmit = async (event) => {
    //     event.preventDefault();
    //     const payLoad = {
    //         uniqueIdentifier: connection?.uniqueId,
    //         userName: userData.userName,
    //         userPassword: encryptor(userData.userPassword, connection?.intKey),

    //         intKey: connection?.intKey,
    //     };

    //     if (!formButtonDisable) {
    //         setInProcess(true);
    //         try {
    //             const { data } = await axiosSWLogIn.post(
    //                 "Authentication/pos/ValidateCashierCredentials",
    //                 payLoad
    //             );
    //             if (data.statusOk) {
    //                 if (
    //                     (data.role =
    //                         "Supervisor" || data.role === "Administrator")
    //                 )
    //                     setIsAdmin(true);
    //                 if (data.role === "Administrator") setISSuperUser(true);
    //             }

    //             setSuccess(true);
    //             setInProcess(false);
    //         } catch (error) {
    //             setInProcess(false);
    //             handleError(error);
    //         }
    //     }
    // };

    /**
     * Submit handle
     * Encrypt the user password
     * and submit the user input and the intKey to the SC server end point for authintication
     * the intKey is the key that is used to encrypt the user password
     * @param {event} event the event object
     */
    const handleSubmit = async (event) => {
        event.preventDefault();
        const payLoad = {
            userName: userData.userName,
            userPassword: encryptor(userData.userPassword, connection?.intKey),

            intKey: connection?.intKey,
        };
        if (!formButtonDisable) {
            setInProcess(true);
            try {
                const { data } = await axiosSC.post(
                    "/auth/check-admin",
                    payLoad,
                );

                setIsAdmin(data?.isValid);
                setIsSuperUser(data?.role === "superUser");

                setSuccess(true);
                setInProcess(false);
            } catch (error) {
                setInProcess(false);
                handleError(error);
            }
        }
    };

    /**
     * Change handler
     * @param {event} event the event object
     */
    const handleChange = (event) => {
        setUserData((prevUserData) => ({
            ...prevUserData,
            [event.target.name]: event.target.value,
        }));
    };

    return (
        <>
            {(success && !isAdmin) || inProcess ? (
                //  Show the output window when the authintication is success
                // (data fetched) but the user is not authinticated,
                // this will show the error message,
                // or if inProcess is true, this is for showing the spinner in the OutputWindow
                <div
                    className={`${styles.main_form_container} ${styles.window} v-flex-container`}
                >
                    <div
                        className={`${
                            !inProcess ? styles.output_window_container : ""
                        }  v-flex-container`}
                    >
                        <OutputWindow
                            content={
                                // Show the spinner when inProcess is true
                                inProcess ? (
                                    <Spinner />
                                ) : (
                                    <div
                                        className={`${styles.result_text_container} v-flex-container`}
                                    >
                                        <h1>
                                            {t(`wrong user name or password`)}
                                        </h1>
                                    </div>
                                )
                            }
                            // Set the color of the output window based on the inProcess state
                            color={
                                inProcess
                                    ? currentCustom?.color?.current
                                    : currentCustom?.color?.error
                            }
                        />
                    </div>

                    <div
                        className={`${styles.result_buttons_container} flex-container`}
                    >
                        <Button
                            onClickFuction={handleReset}
                            body={t(`go back`)}
                            type={"button"}
                            disableHTML={inProcess}
                            disabled={inProcess}
                        />
                    </div>
                </div>
            ) : (
                // Show the login form
                <div
                    className={`${styles.main_form_container} v-flex-container`}
                >
                    <div
                        className={`${styles.form_header_container} flex-container`}
                    >
                        <h1 className={`${styles.form_header}`}>
                            {t(`login`)}
                        </h1>
                    </div>
                    <form
                        className={`${styles.form_container} v-flex-container`}
                        onSubmit={handleSubmit}
                        noValidate={true}
                    >
                        <div className={`${styles.form_inputs_container}`}>
                            <InputText
                                ref={userNameRef}
                                letterSpacing={".2rem"}
                                placeholder={t(`required field`)}
                                label={t(`user name`)}
                                inputId="userName"
                                inputValue={userData?.userName}
                                handleChangeFunction={handleChange}
                                required={hoverFlag && !userData?.userName}
                            />
                            <InputText
                                type={"password"}
                                letterSpacing={".2rem"}
                                placeholder={t(`required field`)}
                                label={t(`password`)}
                                inputId="userPassword"
                                inputValue={userData?.userPassword}
                                handleChangeFunction={handleChange}
                                required={hoverFlag && !userData?.userPassword}
                            />
                        </div>
                        <Button
                            body={t(`submit`)}
                            type={"submit"}
                            disabled={formButtonDisable}
                            setHoverFlag={setHoverFlag}
                        />
                    </form>
                </div>
            )}
        </>
    );
};

export default LogIn;
